import { cn, formatCount } from '@/lib/format'
import { Button } from './Button'
import { ChevronLeftIcon, ChevronRightIcon } from './icons'

export interface PaginationProps {
  /** 1-based. */
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  /** Plural noun for the summary, e.g. "tenants". */
  noun?: string
  className?: string
}

/** "1–25 of 112 tenants" with Previous / Next. */
export function Pagination({ page, pageSize, total, onPageChange, noun = 'results', className }: PaginationProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(Math.max(1, page), pages)
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1
  const to = Math.min(total, current * pageSize)

  return (
    <nav aria-label="Pagination" className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <p className="text-xs text-muted tabular" aria-live="polite">
        {total === 0 ? (
          `No ${noun}`
        ) : (
          <>
            <span className="text-ink">
              {formatCount(from)}–{formatCount(to)}
            </span>{' '}
            of {formatCount(total)} {noun}
          </>
        )}
      </p>
      {pages > 1 ? (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => onPageChange(current - 1)} disabled={current <= 1} icon={<ChevronLeftIcon size={14} />}>
            Previous
          </Button>
          <span className="px-1 text-xs text-muted tabular">
            Page {current} of {pages}
          </span>
          <Button size="sm" onClick={() => onPageChange(current + 1)} disabled={current >= pages}>
            Next
            <ChevronRightIcon size={14} />
          </Button>
        </div>
      ) : null}
    </nav>
  )
}
